import { useEffect } from 'react';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Tag } from 'antd';
import type { ColumnFilterItem } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/lib/table';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router';

import { MoreOutlined } from '@ant-design/icons';
import type { MenuInfo } from '@rc-component/menu/lib/interface';

import { UserOrganization, UserRole } from '../../../enum';
import { dateFormatToLocale } from '../../../helpers/dateTime.helper';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useCapturePostHog } from '../../../hooks/useCapturePostHog';
import { TableService } from '../../../service/table.service';
import type { ArchiveAdminServiceType } from '../../modals/archiveAdminClient/archiveAdminClient.service';
import { showSetPasswordModal } from '../../modals/clientSetTempPassword/clientSetTempPasswordSlice';
import { fetchAdminOrganizations } from '../admin-organization/adminOrganizationSlice';
import { setActiveTab } from '../client-profile/clientProfileSlice';
import {
  disableClientSettingsForm,
  fetchAllClientPrograms,
  fetchAllClientTypes,
  fetchAllDiagnosis,
  fetchAllInsuranceCarriers,
} from '../client-upsert/clientUpsertSlice';
import type { AdminClient, AdminDiagnosis } from './adminClientSlice';
import { adminClientSetInternal } from './adminClientSlice';

export const accountStatusFilters: ColumnFilterItem[] = [
  { value: 'created', text: 'Created' },
  { value: 'active', text: 'Active' },
  { value: 'archived', text: 'Archived' },
];

const getBooleanTagColor = (flagValue: boolean) =>
  flagValue === true ? 'green' : flagValue === false ? 'volcano' : '#8c92ac';

export const getAccountStatusColor = (account_status: string) => {
  switch (account_status) {
    case 'created':
      return 'volcano';
    case 'active':
      return 'green';
    case 'archived':
      return '#8c92ac';
  }
};

const actionMenuItems: MenuProps['items'] = [
  {
    label: 'Edit',
    key: 'edit',
  },
  {
    label: 'Set temporary password',
    key: 'setTemporaryPassword',
  },
  {
    label: 'Unarchive',
    key: 'unarchive',
  },
  {
    label: 'Archive',
    key: 'archive',
  },
  {
    label: 'Mark as external',
    key: 'markExternal',
  },
  {
    label: 'Mark as internal',
    key: 'markInternal',
  },
];

const getGenders = (organization_id: string | null): ColumnFilterItem[] => {
  if (organization_id === UserOrganization.OLH) {
    return [
      { value: 'Male', text: 'Male' },
      { value: 'Female', text: 'Female' },
    ];
  }
  return [
    { value: 'Male', text: 'Male' },
    { value: 'Female', text: 'Female' },
    { value: 'Other', text: 'Other' },
    { value: 'Non-binary', text: 'Non-binary' },
    { value: 'Prefer not to say', text: 'Prefer not to say' },
  ];
};

const getRaces = (country: string | null, role: UserRole | null): ColumnFilterItem[] => {
  if (country === 'United States') {
    return [
      { value: 'African-American', text: 'African-American' },
      { value: 'Asian', text: 'Asian' },
      { value: 'Hawaiian/Pacific Islander', text: 'Hawaiian/Pacific Islander' },
      { value: 'Latinx', text: 'Latinx' },
      { value: 'Native American', text: 'Native American' },
      { value: 'White', text: 'White' },
      { value: 'Other', text: 'Other' },
      { value: 'Prefer not to say', text: 'Prefer not to say' },
    ];
  } else if (country === 'Canada') {
    return [
      { value: 'African origins', text: 'African origins' },
      { value: 'Asian origins', text: 'Asian origins' },
      { value: 'Caribbean origins', text: 'Caribbean origins' },
      { value: 'European origins', text: 'European origins' },
      { value: 'Latin, Central and South American origins', text: 'Latin, Central and South American origins' },
      { value: 'North American Aboriginal origins', text: 'North American Aboriginal origins' },
      { value: 'Oceania origins', text: 'Oceania origins' },
      { value: 'Other North American origins', text: 'Other North American origins' },
      { value: 'Prefer not to say', text: 'Prefer not to say' },
    ];
  } else if (role === UserRole.ADMIN) {
    return [
      { value: 'African-American', text: 'African-American (US)' },
      { value: 'African origins', text: 'African origins (CA)' },
      { value: 'Asian', text: 'Asian (US)' },
      { value: 'Asian origins', text: 'Asian origins (CA)' },
      { value: 'Caribbean origins', text: 'Caribbean origins (CA)' },
      { value: 'European origins', text: 'European origins (CA)' },
      { value: 'Hawaiian/Pacific Islander', text: 'Hawaiian/Pacific Islander (US)' },
      { value: 'Latin, Central and South American origins', text: 'Latin, Central and South American origins (CA)' },
      { value: 'Latinx', text: 'Latinx (US)' },
      { value: 'Native American', text: 'Native American (US)' },
      { value: 'North American Aboriginal origins', text: 'North American Aboriginal origins (CA)' },
      { value: 'Oceania origins', text: 'Oceania origins (CA)' },
      { value: 'Other North American origins', text: 'Other North American origins (CA)' },
      { value: 'White', text: 'White (US)' },
      { value: 'Other', text: 'Other (US)' },
      { value: 'Prefer not to say', text: 'Prefer not to say' },
    ];
  } else {
    return [];
  }
};

export const GetAdminColumns = ({
  setArchiveAdminData,
  setUnArchiveAdminData,
}: Pick<ReturnType<ArchiveAdminServiceType>, 'setArchiveAdminData' | 'setUnArchiveAdminData'>) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getColumnSearchProps, clearSearch } = TableService();
  const { capturePostHogCustomEvent } = useCapturePostHog();
  const sortedInfo = useAppSelector((state) => state.adminClientSlice.requestAdminClientParam.sortedInfo);
  const filteredInfo = useAppSelector((state) => state.adminClientSlice.requestAdminClientParam.filteredInfo);
  const adminOrganizations = useAppSelector((state) => state.adminOrganizationSlice.adminOrganizationsData);
  const insuranceCarriers = useAppSelector((state) => state.clientUpsertSlice.insuranceCarriers);
  const clientPrograms = useAppSelector((state) => state.clientUpsertSlice.clientPrograms);
  const clientTypes = useAppSelector((state) => state.clientUpsertSlice.clientTypes);
  const authResult = useAppSelector((state) => state.authSlice.authResult);
  const currentUser = useAppSelector((state) => state.authSlice.currentUser);
  const diagnoses = useAppSelector((state) => state.clientUpsertSlice.diagnoses);
  const diagnosisFilters = diagnoses.map<ColumnFilterItem>((d) => ({
    value: d.diagnosis_id,
    text: d.diagnosis_name,
  }));
  diagnosisFilters.push({ value: '', text: 'Undiagnosed' });

  useEffect(() => {
    dispatch(fetchAllDiagnosis());
    if (authResult.use_insurance_carrier) {
      dispatch(fetchAllInsuranceCarriers());
    }
    if (authResult.use_client_program) {
      dispatch(fetchAllClientPrograms());
    }
    if (authResult.use_client_type) {
      dispatch(fetchAllClientTypes());
    }
    if (currentUser.role === UserRole.ADMIN) {
      dispatch(fetchAdminOrganizations());
    }
  }, [
    dispatch,
    authResult.use_insurance_carrier,
    authResult.use_client_program,
    authResult.use_client_type,
    currentUser.role,
  ]);

  const getMenuItems = (adminClient: AdminClient) => {
    return actionMenuItems.filter((item) => {
      switch (item?.key) {
        case 'edit':
        case 'setTemporaryPassword':
        case 'archive':
          return adminClient.account_status !== 'archived';
        case 'unarchive':
          return adminClient.account_status === 'archived';
        case 'markExternal':
          return currentUser.role === UserRole.ADMIN && adminClient.internal;
        case 'markInternal':
          return currentUser.role === UserRole.ADMIN && !adminClient.internal;
        default:
          return true;
      }
    });
  };

  const onMenuClick = (e: MenuInfo, adminClient: AdminClient) => {
    e.domEvent.stopPropagation();
    capturePostHogCustomEvent(`OnMenuClick AdminClientTable clicked on ${e.key}`);
    switch (e.key) {
      case 'edit':
        if (currentUser.role === UserRole.ADMIN) {
          navigate(`/page/admin-client-profile/${adminClient.id}`);
        } else {
          navigate(`/page/client-profile/${adminClient.id}`);
          dispatch(setActiveTab('settings'));
        }
        dispatch(disableClientSettingsForm(false));
        break;
      case 'setTemporaryPassword':
        dispatch(
          showSetPasswordModal({
            id: adminClient.id,
            first_name: adminClient.first_name,
            last_name: adminClient.last_name,
            has_submission: adminClient.has_submission,
          }),
        );
        break;
      case 'unarchive':
        setUnArchiveAdminData(
          {
            id: adminClient.id,
            first_name: adminClient.first_name,
            last_name: adminClient.last_name,
          },
          'created',
          'Unarchive',
          'v2_admin_client_status_set',
        );
        break;
      case 'archive':
        setArchiveAdminData({
          id: adminClient.id,
          first_name: adminClient.first_name,
          last_name: adminClient.last_name,
        });
        break;
      case 'markExternal':
      case 'markInternal':
        if (adminClient.id) {
          dispatch(adminClientSetInternal({ client_id: adminClient.id, internal: !adminClient.internal }));
        }
        break;
    }
  };

  const regularColumns: ColumnsType<AdminClient> = [
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      width: currentUser.role !== UserRole.ADMIN ? 55 : 70,
      fixed: 'left',
      render: (_, adminClient) => (
        <Dropdown
          menu={{ items: getMenuItems(adminClient), onClick: (e) => onMenuClick(e, adminClient) }}
          trigger={['click']}
        >
          <Button onClick={(e: MouseEvent) => e.stopPropagation()} style={{ padding: 3 }}>
            <MoreOutlined style={{ fontWeight: 800, fontSize: 25 }} />
          </Button>
        </Dropdown>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 120,
      fixed: 'left',
      ...getColumnSearchProps('name', filteredInfo.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.name || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 150,
      sorter: true,
      ...getColumnSearchProps('email', filteredInfo.email),
      sortOrder: sortedInfo.field === 'email' ? sortedInfo.order : null,
      filteredValue: filteredInfo.email || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Mobile phone',
      dataIndex: 'mobile_phone',
      key: 'mobile_phone',
      width: 110,
      sorter: true,
      ...getColumnSearchProps('mobile_phone', filteredInfo.email),
      sortOrder: sortedInfo.field === 'mobile_phone' ? sortedInfo.order : null,
      filteredValue: filteredInfo.mobile_phone || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Account status',
      dataIndex: 'account_status',
      key: 'account_status',
      filters: accountStatusFilters,
      width: 100,
      render: (account_status: string) => (
        <Tag color={getAccountStatusColor(account_status)} style={{ border: 'none', textTransform: 'capitalize' }}>
          {account_status}
        </Tag>
      ),
      filteredValue: filteredInfo.account_status || null,
    },
    {
      title: 'Internal',
      dataIndex: 'internal',
      key: 'internal',
      filters: [
        { value: 'true', text: 'Internal' },
        { value: 'false', text: 'External' },
      ],
      width: 70,
      render: (internal: boolean) => {
        return (
          <Tag color={internal ? 'grey' : 'green'} style={{ border: 'none', textTransform: 'capitalize' }}>
            {internal ? 'Internal' : 'External'}
          </Tag>
        );
      },
      filteredValue: filteredInfo.internal || null,
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      sorter: true,
      ...getColumnSearchProps('created', filteredInfo.created),
      sortOrder: sortedInfo.field === 'created' ? sortedInfo.order : null,
      filteredValue: filteredInfo.created || null,
      render: (_text: string, record) => (record.created ? dateFormatToLocale(new Date(record.created)) : null),
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
      width: 100,
      filters: adminOrganizations?.map((o) => ({ value: o.id || '', text: o.name })),
      filteredValue: filteredInfo.organization || null,
      render: (_organization, client) => (
        <Tag
          color={client.organization_internal ? 'grey' : 'green'}
          style={{ border: 'none', textTransform: 'capitalize' }}
        >
          {client.organization}
        </Tag>
      ),
    },
    {
      title: 'Assigned clinicians',
      dataIndex: 'clinicians',
      key: 'clinicians',
      width: 150,
      sorter: true,
      ...getColumnSearchProps('clinicians', filteredInfo.clinicians),
      render: (clinicians: string[]) => (
        <div>
          {clinicians?.map((clinician, index) =>
            clinician ? (
              <Tag key={index} color="geekblue" style={{ border: 'none' }}>
                {clinician}
              </Tag>
            ) : (
              <Tag key={index} color="volcano" style={{ border: 'none' }}>
                No clinicians assigned
              </Tag>
            ),
          )}
        </div>
      ),
      sortOrder: sortedInfo.field === 'clinicians' ? sortedInfo.order : null,
      filteredValue: filteredInfo.clinicians || null,
    },
    {
      title: 'Waitlist service',
      dataIndex: 'custom_fields',
      key: 'custom_fields',
      filters: [
        { value: 'Individual', text: 'Individual' },
        { value: 'Group', text: 'Group' },
        { value: 'Both (Individual & Group)', text: 'Both (Individual & Group)' },
        { value: 'iOSI', text: 'iOSI' },
      ],
      width: 120,
      render: (custom_fields: { waitlist_service: string }) => {
        return (
          <div>
            {custom_fields?.waitlist_service ? (
              <Tag color="geekblue" style={{ border: 'none' }}>
                {custom_fields.waitlist_service}
              </Tag>
            ) : null}
          </div>
        );
      },
      filteredValue: filteredInfo.custom_fields ? filteredInfo.custom_fields : null,
    },
    {
      title: 'Sites',
      dataIndex: 'sites',
      key: 'sites',
      width: 100,
      sorter: true,
      ...getColumnSearchProps('sites', filteredInfo.sites),
      render: (sites: string[]) => (
        <div>
          {sites?.map((site, index) =>
            site ? (
              <Tag key={index} color="geekblue" style={{ border: 'none' }}>
                {site}
              </Tag>
            ) : null,
          )}
        </div>
      ),
      sortOrder: sortedInfo.field === 'sites' ? sortedInfo.order : null,
      filteredValue: filteredInfo.sites || null,
    },
    {
      title: 'Health insurance',
      dataIndex: 'insurance_carrier_name',
      key: 'insurance_carrier_name',
      width: 110,
      filters: insuranceCarriers?.map((i) => ({ value: i.insurance_carrier_name, text: i.insurance_carrier_name })),
      filteredValue: filteredInfo.insurance_carrier_name || null,
    },
    {
      title: 'Client type',
      dataIndex: 'client_type',
      key: 'client_type',
      width: 110,
      filters: clientTypes?.map((i) => ({ value: i.client_type, text: i.client_type })),
      filteredValue: filteredInfo.client_type || null,
    },
    {
      title: 'Program',
      dataIndex: 'client_program',
      key: 'client_program',
      width: 110,
      filters: clientPrograms?.map((i) => ({ value: i.client_program, text: i.client_program })),
      filteredValue: filteredInfo.client_program || null,
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      width: 110,
      sorter: true,
      ...getColumnSearchProps('products', filteredInfo.products),
      render: (products: string[]) => (
        <div>
          {products?.map((product_name, index) =>
            product_name ? (
              <Tag key={index} color="geekblue" style={{ border: 'none' }}>
                {product_name}
              </Tag>
            ) : null,
          )}
        </div>
      ),
      sortOrder: sortedInfo.field === 'products' ? sortedInfo.order : null,
      filteredValue: filteredInfo.products || null,
    },
    {
      title: 'Primary diagnosis',
      dataIndex: 'diagnoses',
      key: 'diagnoses',
      width: 110,
      render: (diagnoses: AdminDiagnosis[]) => diagnoses?.map(({ name }) => name),
      filteredValue: filteredInfo.diagnoses || null,
      filters: diagnosisFilters,
      className: 'ph-no-capture',
    },
    {
      title: 'MR#',
      dataIndex: 'medical_record_number',
      width: 100,
      key: 'medical_record_number',
      sorter: true,
      ...getColumnSearchProps('medical_record_number', filteredInfo.medical_record_number),
      sortOrder: sortedInfo.field === 'medical_record_number' ? sortedInfo.order : null,
      filteredValue: filteredInfo.medical_record_number || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Patient consent',
      dataIndex: 'patient_consent_form_acknowledgement',
      key: 'patient_consent_form_acknowledgement',
      width: 90,
      render: (patient_consent: boolean) => {
        return (
          <Tag color={getBooleanTagColor(patient_consent)} style={{ border: 'none' }}>
            {patient_consent ? 'True' : patient_consent === null ? 'Initial' : 'False'}
          </Tag>
        );
      },
      filters: [
        { value: 'true', text: 'True' },
        { value: 'false', text: 'False' },
        { value: 'null', text: 'Initial' },
      ],
      filteredValue: filteredInfo.patient_consent_form_acknowledgement || null,
    },
    {
      title: 'Race',
      dataIndex: 'race',
      key: 'race',
      width: 120,
      filters: getRaces(authResult.country, currentUser.role),
      filteredValue: filteredInfo.race || null,
      className: 'ph-no-capture',
    },
    {
      title: currentUser.organization_id === UserOrganization.OLH ? 'Sex' : 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      width: 90,
      filters: getGenders(currentUser.organization_id),
      filteredValue: filteredInfo.gender || null,
      className: 'ph-no-capture',
    },
  ];

  const columns = regularColumns.filter(({ key }) => {
    switch (key) {
      case 'custom_fields':
        return currentUser.organization_id === UserOrganization.OSI;
      case 'insurance_carrier_name':
        return authResult.use_insurance_carrier;
      case 'client_program':
        return authResult.use_client_program;
      case 'patient_consent_form_acknowledgement':
        return authResult.use_apa_features;
      case 'client_type':
        return authResult.use_client_type;
      case 'organization':
      case 'internal':
        return currentUser.role === UserRole.ADMIN;

      case 'action':
      case 'email':
      case 'clinicians':
      case 'products':
      case 'diagnoses':
      case 'medical_record_number':
      case 'race':
        return currentUser.organization_id !== UserOrganization.OLH;
      case 'mobile_phone':
        return currentUser.role === UserRole.ADMIN || currentUser.organization_id === UserOrganization.OLH;

      default:
        return true;
    }
  });

  return { columns, clearSearch };
};
